import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { marca, nomePdf } from "../marca";
import { TrendingUp, TrendingDown, Wallet } from "lucide-react";

const fmt = (v) => `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;
const fmtCurto = (v) => {
  const n = Number(v||0);
  if (n>=1000000) return `${(n/1000000).toFixed(1)}M`;
  if (n>=1000) return `${(n/1000).toFixed(0)}k`;
  return String(Math.round(n));
};

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const CORES_PIZZA = ["#EE6A1A","#579bfc","#EBA13A","#a25ddc","#1D9E75","#E24B4A","#3B7FD6","#8A5A12","#0A7A4A","#6B36A0"];

function ymd(d){ const s=String(d); const m=s.match(/^(\d{4})-(\d{2})-(\d{2})/); if(m) return {a:+m[1],me:+m[2]}; const dt=new Date(d); return {a:dt.getFullYear(),me:dt.getMonth()+1}; }
function fmtDataBR(d){ if(!d) return "—"; const s=String(d); const m=s.match(/^(\d{4})-(\d{2})-(\d{2})/); if(m) return `${m[3]}/${m[2]}/${m[1]}`; return "—"; }
const STATUS_LABEL = { pago:{t:"Pago",c:"#1D9E75"}, pendente:{t:"Pendente",c:"#98A2B3"}, vencido:{t:"Vencido",c:"#E24B4A"}, cancelado:{t:"Cancelado",c:"#98A2B3"} };

export default function Relatorios() {
  const hoje = new Date();
  const [modo, setModo] = useState("mes");
  const [filtroCat, setFiltroCat] = useState("");
  const [filtroCli, setFiltroCli] = useState("");
  const [visao, setVisao] = useState("caixa");
  const [tipoVis, setTipoVis] = useState("tudo");
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth()+1);
  const [de, setDe] = useState(`${hoje.getFullYear()}-01-01`);
  const [ate, setAte] = useState(new Date().toISOString().slice(0,10));
  const [lancs, setLancs] = useState([]);
  const [lancsAno, setLancsAno] = useState([]);
  const [load, setLoad] = useState(false);

  const periodo = modo==="mes"
    ? { de:`${ano}-${String(mes).padStart(2,"0")}-01`, ate:`${ano}-${String(mes).padStart(2,"0")}-${new Date(ano,mes,0).getDate()}` }
    : modo==="ano" ? { de:`${ano}-01-01`, ate:`${ano}-12-31` } : { de, ate };

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const [rPer, rAno] = await Promise.all([
        api.get(`/financeiro?de=${periodo.de}&ate=${periodo.ate}&campoData=dataPagamento&limit=100000`),
        api.get(`/financeiro?de=${ano}-01-01&ate=${ano}-12-31&campoData=dataEmissao&limit=100000`),
      ]);
      const arr = (d)=> Array.isArray(d) ? d : (d.lancamentos||[]);
      setLancs(arr(rPer.data));
      setLancsAno(arr(rAno.data));
    } catch(e){ console.error(e); setLancs([]); setLancsAno([]); }
    setLoad(false);
  }, [periodo.de, periodo.ate, ano]);

  useEffect(()=>{ carregar(); }, [carregar]);

  const dataRef = (l) => visao==="caixa" ? String(l.dataPagamento||"").slice(0,10) : String(l.dataVencimento||l.dataEmissao||"").slice(0,10);
  const dentroPeriodo = (l) => { const d=dataRef(l); return d>=periodo.de && d<=periodo.ate; };
  const lancsPeriodo = visao==="caixa"
    ? lancsAno.filter(l=>l.status==="pago" && dentroPeriodo(l))
    : lancsAno.filter(l=>l.status!=="cancelado" && dentroPeriodo(l));
  const entradas = lancsPeriodo.filter(l=>l.tipo==="entrada").sort((a,b)=>dataRef(a).localeCompare(dataRef(b)));
  const saidas   = lancsPeriodo.filter(l=>l.tipo==="saida").sort((a,b)=>dataRef(a).localeCompare(dataRef(b)));
  const categoriasTodas = [...new Set(lancsPeriodo.map(l=>l.categoria).filter(Boolean))].sort();
  const clientesTodos = [...new Set(lancsPeriodo.map(l=>l.clienteNome).filter(Boolean))].sort();
  const totalReceber = entradas.reduce((a,l)=>a+Number(l.valor||0),0);
  const totalPagar   = saidas.reduce((a,l)=>a+Number(l.valor||0),0);
  const recebido = entradas.filter(l=>l.status==="pago").reduce((a,l)=>a+Number(l.valor||0),0);
  const pago     = saidas.filter(l=>l.status==="pago").reduce((a,l)=>a+Number(l.valor||0),0);
  const saldo = totalReceber - totalPagar;
  const ehCaixa = visao==="caixa";
  const lblRec = ehCaixa ? "Recebido" : "A receber";
  const lblPag = ehCaixa ? "Pago" : "A pagar";
  const lblRes = ehCaixa ? "Resultado" : "Saldo previsto";
  const tEnt = ehCaixa ? "Entradas (recebidas)" : "Entradas (previstas)";
  const tSai = ehCaixa ? "Saídas (pagas)" : "Saídas (previstas)";
  const tEntPdf = ehCaixa ? "Entradas recebidas" : "Entradas previstas";
  const tSaiPdf = ehCaixa ? "Saidas pagas" : "Saidas previstas";

  const cards = [
    { label:lblRec, val:totalReceber, cor:"#1D9E75", Ico:TrendingUp },
    { label:lblPag, val:totalPagar,   cor:"#E24B4A", Ico:TrendingDown },
    { label:lblRes, val:saldo,        cor:saldo>=0?"#579bfc":"#E24B4A", Ico:Wallet },
  ];

  // barras por mes (ano todo)
  const porMes = MESES.map((_,i)=>{
    const mm = i+1;
    const rec = lancsAno.filter(l=>{ const d=ymd(l.dataPagamento||l.dataVencimento||l.dataEmissao); return l.status==="pago" && l.tipo==="entrada" && d.me===mm && d.a===ano; }).reduce((a,l)=>a+Number(l.valor||0),0);
    const pag = lancsAno.filter(l=>{ const d=ymd(l.dataPagamento||l.dataVencimento||l.dataEmissao); return l.status==="pago" && l.tipo==="saida" && d.me===mm && d.a===ano; }).reduce((a,l)=>a+Number(l.valor||0),0);
    return { rec, pag };
  });
  const maxBar = Math.max(1, ...porMes.map(m=>Math.max(m.rec,m.pag)));

  // pizza de categorias (despesas do periodo)
  const catMap = {};
  saidas.forEach(l=>{ const c=l.categoria||"Outros"; catMap[c]=(catMap[c]||0)+Number(l.valor||0); });
  const cats = Object.entries(catMap).map(([nome,val])=>({nome,val})).sort((a,b)=>b.val-a.val);
  const totalCat = cats.reduce((a,c)=>a+c.val,0)||1;

  // carrega script de CDN sob demanda
  const loadScript = (src) => new Promise((res,rej)=>{ if(document.querySelector(`script[src="${src}"]`)){res();return;} const sc=document.createElement("script"); sc.src=src; sc.onload=res; sc.onerror=rej; document.head.appendChild(sc); });

  const nomePeriodo = modo==="mes" ? `${MESES[mes-1]}-${ano}` : modo==="ano" ? `${ano}` : `${periodo.de}_a_${periodo.ate}`;

  const exportarExcel = async () => {
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    const resumo = [
      ["Relatório Financeiro - Optimus Tecnologia"],
      ["Período", nomePeriodo],
      [],
      ["A Receber", totalReceber],
      ["A Pagar", totalPagar],
      ["Saldo", saldo],
      ["Recebido", recebido],
      ["Pago", pago],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumo), "Resumo");
    const linhas = (arr) => [["Descrição","Cliente/Fornecedor","Categoria","Data","Valor","Status"], ...arr.map(l=>[l.descricao||"", l.clienteNome||"", l.categoria||"", fmtDataBR(l.dataPagamento||l.dataVencimento||l.dataEmissao), Number(l.valor||0), l.status||""])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(linhas(entradas)), "A Receber");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(linhas(saidas)), "A Pagar");
    const catRows = [["Categoria","Valor","%"], ...cats.map(c=>[c.nome, c.val, Math.round(c.val/totalCat*100)+"%"])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(catRows), "Categorias");
    XLSX.writeFile(wb, `Relatorio_${nomePeriodo}.xlsx`);
  };

  const exportarPDF = async () => {
    await loadScript("/vendor/jspdf.umd.min.js");
    await loadScript("/vendor/jspdf.plugin.autotable.min.js");
    let { jsPDF } = window.jspdf;
    let doc = new jsPDF({ orientation: "landscape" });
    if (typeof doc.autoTable !== "function") {
      await new Promise((r,j)=>{ const el=document.createElement("script"); el.src="/vendor/jspdf.plugin.autotable.min.js?v="+Date.now(); el.onload=r; el.onerror=j; document.body.appendChild(el); });
      jsPDF = window.jspdf.jsPDF; doc = new jsPDF({ orientation: "landscape" });
    }
    const W = doc.internal.pageSize.getWidth();
    const M = 14;
    const G=[29,158,117], R=[226,75,74], LARANJA=[238,106,26], CINZA=[120,120,120];
    const moeda = (n)=>"R$ "+Number(n||0).toLocaleString("pt-BR",{minimumFractionDigits:2});
    doc.setFontSize(8); doc.setTextColor(...CINZA); doc.setFont(undefined,"normal");
    doc.text(nomePdf(await marca()), M, 11);
    doc.setFontSize(15); doc.setTextColor(...LARANJA); doc.setFont(undefined,"bold");
    doc.text("Relatorio Financeiro", M, 18);
    doc.setFontSize(8); doc.setTextColor(...CINZA); doc.setFont(undefined,"normal");
    doc.text("Periodo: " + nomePeriodo, M, 23);
    const cardY=27, cardH=16, gap=6, cardW=(W-2*M-2*gap)/3;
    [[lblRec.toUpperCase(), totalReceber, G],[lblPag.toUpperCase(), totalPagar, R],[lblRes.toUpperCase(), saldo, saldo>=0?G:R]].forEach(([lbl,val,cor],i)=>{
      const x=M+i*(cardW+gap);
      doc.setFillColor(248,249,250); doc.setDrawColor(230,230,232);
      doc.roundedRect(x, cardY, cardW, cardH, 1.8, 1.8, "FD");
      doc.setFillColor(...cor); doc.rect(x, cardY, 1.8, cardH, "F");
      doc.setFontSize(7); doc.setTextColor(...CINZA); doc.setFont(undefined,"bold");
      doc.text(lbl, x+7, cardY+6);
      doc.setFontSize(13.5); doc.setTextColor(...cor);
      doc.text(moeda(val), x+7, cardY+12.5);
    });
    const corpo = (arr) => arr.map(l=>[l.descricao||"", l.clienteNome||"-", l.categoria||"", fmtDataBR(l.dataPagamento||l.dataVencimento||l.dataEmissao), moeda(l.valor), l.status||""]);
    const colTab = { 0:{halign:"left"}, 1:{cellWidth:46}, 2:{cellWidth:34}, 3:{cellWidth:26,halign:"center"}, 4:{cellWidth:34,halign:"right"}, 5:{cellWidth:24,halign:"center"} };
    const estiloTab = { fontSize:8, cellPadding:{top:1.5,right:3,bottom:1.5,left:3}, lineColor:[236,236,238], lineWidth:0.1, textColor:[55,55,60] };
    let cursorY = cardY + cardH + 2;
    const secao = (titulo, arr, cor, corFoot, corFootTxt) => {
      const yTit = cursorY + 9;
      doc.setFontSize(9); doc.setTextColor(...LARANJA); doc.setFont(undefined,"bold");
      doc.text(titulo, M, yTit);
      doc.autoTable({
        startY: yTit+2,
        head:[["Descricao","Cliente/Fornecedor","Categoria","Data","Valor","Status"]],
        body: corpo(arr),
        foot:[[{content:"TOTAL",colSpan:4,styles:{halign:"right",fontStyle:"bold"}},{content:moeda(arr.reduce((a,l)=>a+Number(l.valor||0),0)),styles:{halign:"right",fontStyle:"bold"}},{content:""}]],
        theme:"grid", styles:estiloTab, alternateRowStyles:{fillColor:[250,250,251]},
        headStyles:{ fillColor:cor, textColor:[255,255,255], fontStyle:"bold" },
        footStyles:{ fillColor:corFoot, textColor:corFootTxt, fontStyle:"bold" },
        columnStyles: colTab, margin:{left:M,right:M},
      });
      cursorY = doc.lastAutoTable.finalY;
    };
    secao(tEntPdf, entradas, G, [232,244,238], [15,110,86]);
    secao(tSaiPdf, saidas, R, [250,235,235], [120,30,30]);
    const yc = cursorY + 9;
    doc.setFontSize(9); doc.setTextColor(...LARANJA); doc.setFont(undefined,"bold");
    doc.text("Despesas por Categoria", M, yc);
    doc.autoTable({ startY: yc+2, head:[["Categoria","Valor","%"]], body:cats.map(c=>[c.nome, moeda(c.val), Math.round(c.val/totalCat*100)+"%"]), theme:"grid", styles:estiloTab, alternateRowStyles:{fillColor:[250,250,251]}, headStyles:{fillColor:LARANJA,textColor:[255,255,255],fontStyle:"bold"}, margin:{left:M,right:M}, columnStyles:{1:{halign:"right"},2:{halign:"right",cellWidth:30}} });
    doc.save(`Relatorio_${nomePeriodo}.pdf`);
  };

  const btn = (ativo) => ({ padding:"7px 16px", borderRadius:8, fontSize:13, fontWeight:ativo?600:400, cursor:"pointer", border:"1px solid var(--border-color)", background:ativo?"#EE6A1A":"transparent", color:ativo?"var(--card-bg)":"var(--text-secondary)" });
  const inpData = { padding:"7px 10px", borderRadius:8, border:"1px solid var(--border-color)", background:"var(--input-bg)", color:"var(--text-primary)", fontSize:13, fontFamily:"'Inter',sans-serif" };
  const cardBox = { background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:12, padding:20, marginBottom:22 };

  // pizza: calcular offsets
  const R=60, C=2*Math.PI*R;
  let acum=0;
  const fatias = cats.map((c,i)=>{ const frac=c.val/totalCat; const dash=frac*C; const off=-acum*C; acum+=frac; return { ...c, dash, off, cor:CORES_PIZZA[i%CORES_PIZZA.length], pct:Math.round(frac*100) }; });

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", padding:"8px 32px 48px", background:"var(--page-bg)", minHeight:"100vh", color:"var(--text-primary)" }}>
      <h1 style={{ fontSize:24, fontWeight:800, color:"var(--text-primary)", marginBottom:4 }}>Relatórios</h1>
      <p style={{ color:"var(--text-muted)", fontSize:14, marginBottom:22 }}>{ehCaixa ? "Detalhado item a item — regime de caixa (só o que foi pago/recebido no mês)" : "Detalhado item a item — previsto (a vencer no mês, inclui em aberto)"}</p>

      <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
        <button onClick={()=>setModo("mes")} style={btn(modo==="mes")}>Mensal</button>
        <button onClick={()=>setModo("ano")} style={btn(modo==="ano")}>Anual</button>
        <button onClick={()=>setModo("custom")} style={btn(modo==="custom")}>Intervalo</button>
        <span style={{ width:1, height:22, background:"var(--border-color)", margin:"0 6px" }}/>
        <button onClick={()=>setVisao("caixa")} style={btn(visao==="caixa")}>Caixa (pago)</button>
        <button onClick={()=>setVisao("previsto")} style={btn(visao==="previsto")}>Previsto (a vencer)</button>
        <span style={{ width:1, height:20, background:"var(--border-color)", margin:"0 6px", alignSelf:"center" }}></span>
        <button onClick={()=>setTipoVis("tudo")} style={btn(tipoVis==="tudo")}>Tudo</button>
        <button onClick={()=>setTipoVis("entrada")} style={btn(tipoVis==="entrada")}>Só a receber</button>
        <button onClick={()=>setTipoVis("saida")} style={btn(tipoVis==="saida")}>Só a pagar</button>
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <button onClick={exportarPDF} style={{ padding:"7px 16px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid #E24B4A", background:"transparent", color:"#E24B4A", display:"flex", alignItems:"center", gap:6 }}>📄 Exportar PDF</button>
          <button onClick={exportarExcel} style={{ padding:"7px 16px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", border:"1px solid #1D9E75", background:"transparent", color:"#1D9E75", display:"flex", alignItems:"center", gap:6 }}>📊 Exportar Excel</button>
        </div>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:22, alignItems:"center", flexWrap:"wrap" }}>
        {modo==="mes" && (<>
          <select value={mes} onChange={e=>setMes(+e.target.value)} style={{...inpData, appearance:"auto"}}>{MESES.map((m,i)=><option key={i} value={i+1}>{m}</option>)}</select>
          <select value={ano} onChange={e=>setAno(+e.target.value)} style={{...inpData, appearance:"auto"}}>{[hoje.getFullYear(),hoje.getFullYear()-1,hoje.getFullYear()-2].map(a=><option key={a} value={a}>{a}</option>)}</select>
        </>)}
        {modo==="ano" && (<select value={ano} onChange={e=>setAno(+e.target.value)} style={{...inpData, appearance:"auto"}}>{[hoje.getFullYear(),hoje.getFullYear()-1,hoje.getFullYear()-2].map(a=><option key={a} value={a}>{a}</option>)}</select>)}
        {modo==="custom" && (<>
          <input type="date" value={de} onChange={e=>setDe(e.target.value)} style={inpData}/>
          <span style={{ color:"var(--text-muted)" }}>até</span>
          <input type="date" value={ate} onChange={e=>setAte(e.target.value)} style={inpData}/>
        </>)}
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:22 }}>
        {cards.map(({label,val,cor,Ico})=>(
          <div key={label} style={{ flex:1, background:cor+"14", border:`1px solid ${cor}33`, borderRadius:10, padding:"16px 18px", position:"relative", overflow:"hidden" }}>
            <Ico size={34} color={cor} style={{ position:"absolute", top:14, right:14, opacity:.18 }}/>
            <div style={{ fontSize:22, fontWeight:800, color:cor, lineHeight:1.1, letterSpacing:"-.02em" }}>{fmt(val)}</div>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:8, textTransform:"uppercase", letterSpacing:".04em", fontWeight:600 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* FILTROS */}
      <div style={{ ...cardBox, marginBottom:14, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", padding:14 }}>
        <span style={{ fontSize:13, fontWeight:600, color:"var(--text-secondary)" }}>Categoria:</span>
        <select value={filtroCat} onChange={e=>setFiltroCat(e.target.value)} style={{ ...inpData, appearance:"auto", minWidth:200 }}>
          <option value="">Todas as categorias</option>
          {categoriasTodas.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize:13, fontWeight:600, color:"var(--text-secondary)", marginLeft:6 }}>Cliente/Fornecedor:</span>
        <select value={filtroCli} onChange={e=>setFiltroCli(e.target.value)} style={{ ...inpData, appearance:"auto", minWidth:200 }}>
          <option value="">Todos</option>
          {clientesTodos.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        {(filtroCat||filtroCli) && <button onClick={()=>{ setFiltroCat(""); setFiltroCli(""); }} style={{ padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:"1px solid var(--border-color)", background:"transparent", color:"var(--text-secondary)" }}>Limpar</button>}
      </div>

      {/* TABELAS DETALHADAS */}
      {[[tEnt, entradas, "#1D9E75", "entrada"],[tSai, saidas, "#E24B4A", "saida"]].filter(x=> tipoVis==="tudo" || tipoVis===x[3]).map(([titulo, arr0, cor])=>{
        const arr = arr0.filter(l => (!filtroCat || (l.categoria||"—")===filtroCat) && (!filtroCli || (l.clienteNome||"—")===filtroCli));
        const totalArr = arr.reduce((a,l)=>a+Number(l.valor||0),0);
        return (
        <div key={titulo} style={cardBox}>
          <div style={{ fontSize:13, fontWeight:700, color:cor, marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}><span>{titulo} · {arr.length}{filtroCat?` · ${filtroCat}`:""}{filtroCli?` · ${filtroCli}`:""}</span><span>Total: {fmt(totalArr)}</span></div>
          {arr.length===0 ? (
            <div style={{ color:"var(--text-muted)", fontSize:13 }}>Nenhum lançamento.</div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:640 }}>
                <thead><tr style={{ background:"var(--input-bg)" }}>
                  {["Descrição","Cliente/Fornecedor","Categoria","Vencimento","Valor","Status"].map(h=>(
                    <th key={h} style={{ padding:"9px 12px", fontSize:10, fontWeight:600, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:".05em", textAlign:h==="Valor"?"right":"left", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {arr.map(l=>{ const st=STATUS_LABEL[l.status]||STATUS_LABEL.pendente; return (
                    <tr key={l._id}>
                      <td style={{ padding:"9px 12px", fontSize:12.5, color:"var(--text-primary)", borderTop:"1px solid var(--border-color)" }}>{l.descricao}</td>
                      <td style={{ padding:"9px 12px", fontSize:12.5, color:"var(--text-primary)", fontWeight:500, borderTop:"1px solid var(--border-color)" }}>{l.clienteNome||"—"}</td>
                      <td style={{ padding:"9px 12px", fontSize:12.5, color:"var(--text-secondary)", borderTop:"1px solid var(--border-color)" }}>{l.categoria||"—"}</td>
                      <td style={{ padding:"9px 12px", fontSize:12.5, color:"var(--text-secondary)", borderTop:"1px solid var(--border-color)", whiteSpace:"nowrap" }}>{fmtDataBR(l.dataPagamento||l.dataVencimento||l.dataEmissao)}</td>
                      <td style={{ padding:"9px 12px", fontSize:12.5, color:cor, fontWeight:700, borderTop:"1px solid var(--border-color)", textAlign:"right", whiteSpace:"nowrap" }}>{fmt(l.valor)}</td>
                      <td style={{ padding:"9px 12px", borderTop:"1px solid var(--border-color)" }}><span style={{ background:st.c+"1f", color:st.c, padding:"2px 9px", borderRadius:99, fontSize:10.5, fontWeight:600, whiteSpace:"nowrap" }}>{st.t}</span></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })}

      {load && <div style={{ color:"var(--text-muted)", fontSize:13 }}>Carregando...</div>}
    </div>
  );
}
